import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { customerFuelAPI, fuelPriceAPI } from '../api';
import { toast } from 'sonner';
import { Fuel, Save, Upload, FileText, RefreshCw, Download } from 'lucide-react';

const DEFAULT_CONFIG = {
  enabled: false,
  table_type: 'flat', // 'flat' (dollars) or 'percentage'
  equipment_type: 'DV53',
  week_preference: 'current', // 'current' or 'previous'
  fuel_table: [],
  table_name: ''
};

const EQUIPMENT_OPTIONS = ['DV53', 'DV48', 'Reefer 53', 'Reefer 48', 'Box Truck', 'Flatbed', 'Other'];

export const CustomerFuelSurchargeTab = ({ customerId }) => {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [currentPrice, setCurrentPrice] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [fetchingPrices, setFetchingPrices] = useState(false);


  const fetchConfig = useCallback(async () => {
    try {
      const res = await customerFuelAPI.getConfig(customerId);
      const data = res.data || {};
      setConfig({ ...DEFAULT_CONFIG, ...data });
    } catch (e) {
      toast.error('Failed to load fuel surcharge config');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  const fetchPriceHistory = useCallback(async () => {
    try {
      const res = await fuelPriceAPI.getHistory(6);
      // API returns prices sorted desc; keep the 3 most recent
      const list = Array.isArray(res.data) ? res.data.slice(0, 3) : [];
      setPriceHistory(list);
      if (list[0]) setCurrentPrice(list[0]);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchConfig();
    fetchPriceHistory();
  }, [fetchConfig, fetchPriceHistory]);

  const handleFetchEIAPrices = async () => {
    setFetchingPrices(true);
    try {
      const res = await fuelPriceAPI.fetchPrices();
      const count = res.data?.prices?.length || res.data?.inserted || 0;
      toast.success(`Fetched ${count} EIA price record${count === 1 ? '' : 's'}`);
      await fetchPriceHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to fetch EIA prices');
    } finally {
      setFetchingPrices(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await customerFuelAPI.updateConfig(customerId, config);
      toast.success('Fuel surcharge config saved');
    } catch (e) {
      toast.error('Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  const handlePasteImport = async () => {
    if (!pasteText.trim()) {
      toast.error('Paste a fuel table first');
      return;
    }
    setSaving(true);
    try {
      const res = await customerFuelAPI.pasteTable(customerId, pasteText, `Pasted ${new Date().toLocaleDateString()}`);
      const rows = res.data?.fuel_table || [];
      setConfig(prev => ({
        ...prev,
        fuel_table: rows,
        table_name: res.data?.table_name || prev.table_name
      }));
      setPasteText('');
      toast.success(`${res.data.rows} rows imported${res.data.skipped ? ` (${res.data.skipped} skipped)` : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to parse table');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSaving(true);
    try {
      const res = await customerFuelAPI.uploadTable(customerId, file);
      toast.success(`${res.data.rows} rows uploaded from ${res.data.table_name}`);
      await fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload file');
    } finally {
      setSaving(false);
      e.target.value = '';
    }
  };

  const handleClearTable = () => {
    if (!window.confirm('Clear the entire fuel table?')) return;
    setConfig(prev => ({ ...prev, fuel_table: [], table_name: '' }));
  };

  // Preview current FSC value based on currentPrice
  const previewValue = useMemo(() => {
    if (!currentPrice?.price || !config.fuel_table?.length) return null;
    const match = config.fuel_table.find(e => currentPrice.price >= e.lower && currentPrice.price <= e.upper);
    return match ? match.value : null;
  }, [currentPrice, config.fuel_table]);

  if (loading) {
    return <div className="text-[var(--tms-text-secondary)] p-8">Loading...</div>;
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="fuel-surcharge-tab">
      {/* Config Block */}
      <div className="stats-card">
        <div className="flex items-center gap-2 mb-6">
          <Fuel className="w-5 h-5 text-[var(--tms-primary)]" />
          <h3 className="text-lg font-semibold">Fuel Surcharge Configuration</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="flex items-center gap-2 pt-6">
            <Checkbox
              id="fsc-enabled"
              checked={config.enabled}
              onCheckedChange={(v) => setConfig(prev => ({ ...prev, enabled: !!v }))}
              data-testid="checkbox-fsc-enabled"
            />
            <Label htmlFor="fsc-enabled" className="cursor-pointer font-medium">FSC Enabled</Label>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Equipment Type</Label>
            <Select value={config.equipment_type} onValueChange={(v) => setConfig(prev => ({ ...prev, equipment_type: v }))}>
              <SelectTrigger data-testid="select-equipment-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {EQUIPMENT_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Table Type</Label>
            <Select value={config.table_type} onValueChange={(v) => setConfig(prev => ({ ...prev, table_type: v }))}>
              <SelectTrigger data-testid="select-table-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="flat">Flat $/mile</SelectItem>
                <SelectItem value="percentage">Percentage (%)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Week to Use</Label>
            <Select value={config.week_preference} onValueChange={(v) => setConfig(prev => ({ ...prev, week_preference: v }))}>
              <SelectTrigger data-testid="select-week-preference"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="current">Current Week (of ship date)</SelectItem>
                <SelectItem value="previous">Last Week (1 week before)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Base Fuel Price (shown as reference)</Label>
            <Input
              type="number"
              step="0.001"
              min="0"
              value={config.base_price ?? ''}
              onChange={(e) => setConfig(prev => ({ ...prev, base_price: e.target.value === '' ? null : parseFloat(e.target.value) }))}
              placeholder="e.g. 0.70"
              data-testid="input-fsc-base-price"
            />
          </div>
        </div>

        {priceHistory.length > 0 ? (
          <div className="bg-[var(--tms-hover)] rounded p-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-[var(--tms-text)]">EIA U.S. On-Highway Diesel — Last 3 Weeks</span>
              <div className="flex items-center gap-3">
                {previewValue !== null && (
                  <span className="text-sm" data-testid="fsc-preview">
                    FSC at current price → <strong className="font-mono text-amber-700">
                      {config.table_type === 'percentage' ? `${previewValue}%` : `$${previewValue.toFixed(3)}/mi`}
                    </strong>
                  </span>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleFetchEIAPrices}
                  disabled={fetchingPrices}
                  className="h-7 text-xs"
                  data-testid="refresh-eia-prices-button"
                >
                  <RefreshCw className={`w-3 h-3 mr-1 ${fetchingPrices ? 'animate-spin' : ''}`} />
                  {fetchingPrices ? 'Fetching...' : 'Refresh'}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {priceHistory.map((p, idx) => {
                const matchEntry = config.fuel_table?.find(e => p.price >= e.lower && p.price <= e.upper);
                return (
                  <div
                    key={p.date}
                    className={`border rounded p-2 text-sm ${idx === 0 ? 'bg-amber-50 border-amber-300' : 'bg-[var(--tms-card)]'}`}
                    data-testid={`eia-week-${idx}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-[var(--tms-text-muted)]">
                        Week of {p.date}{idx === 0 ? ' (Current)' : idx === 1 ? ' (-1)' : ' (-2)'}
                      </span>
                    </div>
                    <div className="font-mono text-lg font-bold">${p.price.toFixed(3)}</div>
                    {matchEntry && (
                      <div className="text-xs text-amber-700 font-medium">
                        FSC → {config.table_type === 'percentage' ? `${matchEntry.value}%` : `$${matchEntry.value.toFixed(3)}/mi`}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded p-4 mb-3 flex items-center justify-between" data-testid="eia-empty-state">
            <div>
              <p className="text-sm font-semibold text-amber-900 mb-1">No EIA diesel prices loaded yet</p>
              <p className="text-xs text-amber-800">
                Click "Fetch Latest EIA Prices" to scrape the latest weekly U.S. On-Highway Diesel prices from eia.gov. This populates the 3-week history and enables auto-FSC calculations on loads.
              </p>
            </div>
            <Button
              type="button"
              onClick={handleFetchEIAPrices}
              disabled={fetchingPrices}
              className="ml-3 shrink-0"
              data-testid="fetch-eia-prices-button"
            >
              <Download className={`w-4 h-4 mr-1 ${fetchingPrices ? 'animate-pulse' : ''}`} />
              {fetchingPrices ? 'Fetching...' : 'Fetch Latest EIA Prices'}
            </Button>
          </div>
        )}

        <Button onClick={handleSave} disabled={saving} data-testid="save-fsc-config-button">
          <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </div>

      {/* Import Block */}
      <div className="stats-card">
        <h3 className="text-lg font-semibold mb-4">Import Fuel Table</h3>
        <p className="text-sm text-[var(--tms-text-secondary)] mb-3">
          Paste your fuel table directly. Each row needs 3 numbers: <strong>lower bound</strong>, <strong>upper bound</strong>, and <strong>{config.table_type === 'percentage' ? 'percentage' : 'flat $/mi value'}</strong>.
          Tabs, commas, spaces, or hyphens between numbers all work. Headers and blank lines are skipped.
        </p>
        <Textarea
          value={pasteText}
          onChange={(e) => setPasteText(e.target.value)}
          rows={8}
          placeholder={`Examples:\n3.00  3.08  0.25\n3.09 - 3.15  0.28\n3.16, 3.23, 0.31\n...`}
          className="font-mono text-sm"
          data-testid="textarea-paste-table"
        />
        <div className="flex gap-2 mt-3">
          <Button onClick={handlePasteImport} disabled={saving} data-testid="import-paste-button">
            <FileText className="w-4 h-4 mr-1" /> Parse & Import Pasted Table
          </Button>
          <label className="inline-flex items-center">
            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFileUpload} data-testid="upload-file-input" />
            <span className="inline-flex items-center px-3 py-2 text-sm border rounded cursor-pointer hover:bg-[var(--tms-hover)]">
              <Upload className="w-4 h-4 mr-1" /> Or Upload Excel (.xlsx)
            </span>
          </label>
        </div>
      </div>

      {/* Current Table */}
      <div className="stats-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">
            Current Fuel Table
            {config.table_name && (
              <span className="text-xs text-[var(--tms-text-muted)] ml-2 font-normal">({config.table_name})</span>
            )}
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-sm text-[var(--tms-text-muted)]">{config.fuel_table?.length || 0} rows</span>
            {config.fuel_table?.length > 0 && (
              <Button size="sm" variant="outline" onClick={handleClearTable} data-testid="clear-table-button">
                <RefreshCw className="w-4 h-4 mr-1" /> Clear
              </Button>
            )}
          </div>
        </div>

        {(!config.fuel_table || config.fuel_table.length === 0) ? (
          <p className="text-[var(--tms-text-muted)] text-center py-8 border rounded-lg bg-[var(--tms-hover)]">
            No fuel table loaded. Paste or upload one above.
          </p>
        ) : (
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="data-table">
              <thead className="sticky top-0 bg-[var(--tms-card)]">
                <tr>
                  <th>From ($/gal)</th>
                  <th>To ($/gal)</th>
                  <th>{config.table_type === 'percentage' ? 'FSC %' : 'FSC $/mi'}</th>
                </tr>
              </thead>
              <tbody>
                {config.fuel_table.map((entry, idx) => {
                  const isMatch = currentPrice?.price &&
                    currentPrice.price >= entry.lower &&
                    currentPrice.price <= entry.upper;
                  return (
                    <tr
                      key={idx}
                      className={isMatch ? 'bg-amber-50' : ''}
                      data-testid={`fuel-row-${idx}`}
                    >
                      <td className="font-mono">${entry.lower.toFixed(3)}</td>
                      <td className="font-mono">${entry.upper.toFixed(3)}</td>
                      <td className="font-mono font-semibold">
                        {config.table_type === 'percentage' ? `${entry.value}%` : `$${entry.value.toFixed(3)}`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerFuelSurchargeTab;
